// src/pages/Dashboard/Documents.js

import React, { useEffect, useMemo, useState, useCallback } from "react";
import toast from "react-hot-toast";

import "../../styles/Tables.css";
import "../../styles/Documents.css";

import Skeleton from "../../components/Skeleton";
import {
  fetchDocumentsApi,
  downloadDocumentApi,
  downloadDocumentsZipApi,
  getClientName,
} from "../../lib/api";

function bytesToSize(bytes = 0) {
  const b = Number(bytes) || 0;
  if (b <= 0) return "-";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), sizes.length - 1);
  return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function getExt(filename = "") {
  const m = String(filename).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : "";
}

function getTypeLabel(ext) {
  if (!ext) return "File";
  if (ext === "pdf") return "PDF";
  if (["xls", "xlsx"].includes(ext)) return "Excel";
  if (ext === "csv") return "CSV";
  if (["png", "jpg", "jpeg", "webp"].includes(ext)) return "Image";
  return ext.toUpperCase();
}

function safeDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function isThisMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function getYearFromIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

function safeSlug(s = "") {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

// ---- TAGGING (frontend-only, stored in localStorage) ----
const TAGS = ["electricity", "gas", "travel", "waste", "invoice", "water", "fuel", "other"];
const TAG_LABEL = {
  electricity: "Electricity",
  gas: "Gas",
  travel: "Travel",
  waste: "Waste",
  invoice: "Invoice",
  water: "Water",
  fuel: "Fuel",
  other: "Other",
};

function suggestTagFromFilename(filename = "") {
  const f = String(filename).toLowerCase();

  if (f.includes("electric") || f.includes("elec") || f.includes("kwh")) return "electricity";
  if (f.includes("gas") || f.includes("m3") || f.includes("meter")) return "gas";
  if (f.includes("travel") || f.includes("flight") || f.includes("train") || f.includes("taxi") || f.includes("uber"))
    return "travel";
  if (f.includes("waste") || f.includes("recycl") || f.includes("landfill")) return "waste";
  if (f.includes("invoice") || f.includes("inv_") || f.match(/\binv\b/)) return "invoice";
  if (f.includes("water") || f.includes("sewer")) return "water";
  if (f.includes("diesel") || f.includes("petrol") || f.includes("fuel") || f.includes("litre") || f.includes("ltr"))
    return "fuel";

  return "";
}

function storageKey(clientName) {
  return `hs_docs_tags_v1::${clientName || "unknown"}`;
}

function loadTagsMap(clientName) {
  try {
    const raw = localStorage.getItem(storageKey(clientName));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveTagsMap(clientName, map) {
  try {
    localStorage.setItem(storageKey(clientName), JSON.stringify(map || {}));
  } catch {
    // ignore
  }
}

export default function Documents() {
  const clientName = getClientName();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // year selector for ZIP downloads
  const [zipYear, setZipYear] = useState("");

  // ✅ POLISH: per-row action loading
  const [rowBusyId, setRowBusyId] = useState(null); // doc.id/filename
  const [zipBusy, setZipBusy] = useState(false);

  const [tagsMap, setTagsMap] = useState(() => loadTagsMap(clientName));

  useEffect(() => {
    setTagsMap(loadTagsMap(clientName));
  }, [clientName]);

  const loadDocs = useCallback(async () => {
    try {
      setLoading(true);
      const list = await fetchDocumentsApi();
      setDocs(list || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const enrichedDocs = useMemo(() => {
    return (docs || []).map((d) => {
      const filename = d?.filename || d?.id || "";
      const ext = getExt(filename);
      const typeLabel = getTypeLabel(ext);

      const savedTag = tagsMap?.[filename] || "";
      const suggested = suggestTagFromFilename(filename);
      const tag = savedTag || suggested || "";

      return {
        ...d,
        filename,
        ext,
        typeLabel,
        tag,
        tagSource: savedTag ? "saved" : suggested ? "suggested" : "none",
      };
    });
  }, [docs, tagsMap]);

  const availableYears = useMemo(() => {
    const set = new Set();
    for (const d of enrichedDocs) {
      const y = getYearFromIso(d.uploaded_at);
      if (y) set.add(y);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [enrichedDocs]);

  useEffect(() => {
    if (!zipYear && availableYears.length) setZipYear(String(availableYears[0]));
  }, [availableYears, zipYear]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    for (const d of enrichedDocs) set.add(d.typeLabel);
    return ["all", ...Array.from(set).sort()];
  }, [enrichedDocs]);

  const tagOptions = useMemo(() => {
    const set = new Set(TAGS);
    for (const k of Object.keys(tagsMap || {})) {
      const t = tagsMap?.[k];
      if (t) set.add(t);
    }
    return ["all", "untagged", ...Array.from(set).sort()];
  }, [tagsMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let list = enrichedDocs.filter((d) => {
      const matchesQuery = !q || d.filename.toLowerCase().includes(q);
      const matchesType = typeFilter === "all" || d.typeLabel === typeFilter;

      const matchesTag =
        tagFilter === "all"
          ? true
          : tagFilter === "untagged"
          ? !d.tag
          : (d.tag || "").toLowerCase() === tagFilter.toLowerCase();

      return matchesQuery && matchesType && matchesTag;
    });

    const byTime = (x) => new Date(x.uploaded_at || 0).getTime() || 0;
    const bySize = (x) => Number(x.size_bytes || 0) || 0;
    const byName = (x) => (x.filename || "").toLowerCase();

    switch (sortBy) {
      case "oldest":
        list.sort((a, b) => byTime(a) - byTime(b));
        break;
      case "size_desc":
        list.sort((a, b) => bySize(b) - bySize(a));
        break;
      case "size_asc":
        list.sort((a, b) => bySize(a) - bySize(b));
        break;
      case "name_az":
        list.sort((a, b) => byName(a).localeCompare(byName(b)));
        break;
      case "name_za":
        list.sort((a, b) => byName(b).localeCompare(byName(a)));
        break;
      case "newest":
      default:
        list.sort((a, b) => byTime(b) - byTime(a));
        break;
    }

    return list;
  }, [enrichedDocs, query, typeFilter, tagFilter, sortBy]);

  const summary = useMemo(() => {
    const totalCount = enrichedDocs.length;
    const totalBytes = enrichedDocs.reduce((acc, d) => acc + (Number(d.size_bytes) || 0), 0);

    const monthDocs = enrichedDocs.filter((d) => isThisMonth(d.uploaded_at));
    const monthCount = monthDocs.length;
    const monthBytes = monthDocs.reduce((acc, d) => acc + (Number(d.size_bytes) || 0), 0);

    return { totalCount, totalBytes, monthCount, monthBytes };
  }, [enrichedDocs]);

  const setDocTag = (filename, tag) => {
    const cleaned = (tag || "").trim();
    const next = { ...(tagsMap || {}) };

    if (!cleaned) delete next[filename];
    else next[filename] = cleaned;

    setTagsMap(next);
    saveTagsMap(clientName, next);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  };

  const handleDownload = async (doc) => {
    const key = doc?.id || doc?.filename;
    try {
      setRowBusyId(key);
      toast.loading("Downloading…", { id: `dl_${key}` });

      const blob = await downloadDocumentApi(doc.id || doc.filename);
      downloadBlob(blob, doc.filename || "document");

      toast.success("Downloaded", { id: `dl_${key}` });
    } catch (err) {
      toast.error(err?.message || "Download failed", { id: `dl_${key}` });
    } finally {
      setRowBusyId(null);
    }
  };

  const handlePreview = async (doc) => {
    const key = doc?.id || doc?.filename;
    try {
      setRowBusyId(key);
      toast.loading("Opening preview…", { id: `pv_${key}` });

      const blob = await downloadDocumentApi(doc.id || doc.filename);
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Preview opened", { id: `pv_${key}` });

      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || "Preview failed", { id: `pv_${key}` });
    } finally {
      setRowBusyId(null);
    }
  };

  const handleDownloadZip = async () => {
    const yearNum = Number(zipYear);
    if (!yearNum || Number.isNaN(yearNum)) {
      toast.error("Choose a valid year first");
      return;
    }

    try {
      setZipBusy(true);
      toast.loading("Preparing ZIP…", { id: "zip" });

      const blob = await downloadDocumentsZipApi(yearNum);

      const clientSlug = safeSlug(clientName || "");
      const filename = clientSlug ? `documents_${clientSlug}_${yearNum}.zip` : `documents_${yearNum}.zip`;
      downloadBlob(blob, filename);

      toast.success("ZIP downloaded", { id: "zip" });
    } catch (err) {
      toast.error(err?.message || "ZIP download failed", { id: "zip" });
    } finally {
      setZipBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="documents-page">
        <div className="documents-header">
          <div>
            <h1>Documents</h1>
            <p className="subtitle">Loading uploaded files…</p>
          </div>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <Skeleton height={240} />
        </div>
      </div>
    );
  }

  const hasAnyDocs = enrichedDocs.length > 0;

  return (
    <div className="documents-page">
      <div className="documents-header">
        <div>
          <h1>Documents</h1>
          <p className="subtitle">All uploaded evidence (electricity bills, invoices, travel reports, etc.)</p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn-secondary" type="button" onClick={loadDocs}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="documents-summary" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
        <span className="pill pill-soft">
          {summary.monthCount} uploaded this month • {bytesToSize(summary.monthBytes)}
        </span>
        <span className="pill pill-soft">
          {summary.totalCount} total • {bytesToSize(summary.totalBytes)}
        </span>
      </div>

      {/* Controls */}
      <div className="card documents-controls" style={{ marginTop: 10 }}>
        <div className="documents-controls-row" style={{ flexWrap: "wrap" }}>
          <input
            className="documents-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by filename…"
            disabled={!hasAnyDocs}
          />

          <select
            className="documents-filter"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            title="Filter by tag"
            disabled={!hasAnyDocs}
          >
            {tagOptions.map((t) => (
              <option key={t} value={t}>
                {t === "all" ? "All tags" : t === "untagged" ? "Untagged" : TAG_LABEL[t] || t}
              </option>
            ))}
          </select>

          <select
            className="documents-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            title="Filter by file type"
            disabled={!hasAnyDocs}
          >
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {t === "all" ? "All types" : t}
              </option>
            ))}
          </select>

          <select
            className="documents-filter"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort"
            disabled={!hasAnyDocs}
          >
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="size_desc">Sort: Size (largest)</option>
            <option value="size_asc">Sort: Size (smallest)</option>
            <option value="name_az">Sort: Filename (A–Z)</option>
            <option value="name_za">Sort: Filename (Z–A)</option>
          </select>

          {/* ZIP download controls */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="documents-filter"
              value={zipYear}
              onChange={(e) => setZipYear(e.target.value)}
              title="Choose year to download"
              disabled={!availableYears.length || zipBusy}
            >
              {!availableYears.length ? (
                <option value="">No years</option>
              ) : (
                availableYears.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))
              )}
            </select>

            <button
              className="btn-primary"
              type="button"
              onClick={handleDownloadZip}
              disabled={!zipYear || !availableYears.length || zipBusy}
              title="Download all documents for the selected year as a ZIP"
            >
              {zipBusy ? "Preparing…" : "Download year ZIP"}
            </button>
          </div>

          <button
            className="btn-secondary"
            type="button"
            onClick={() => {
              setQuery("");
              setTypeFilter("all");
              setTagFilter("all");
              setSortBy("newest");
            }}
            disabled={!hasAnyDocs || (!query && typeFilter === "all" && tagFilter === "all" && sortBy === "newest")}
          >
            Clear filters
          </button>

          <span className="pill pill-soft" style={{ marginLeft: "auto" }}>
            {filtered.length} document{filtered.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* Empty state */}
      {!hasAnyDocs ? (
        <div className="card" style={{ marginTop: 12, padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>No documents yet</h3>
          <p style={{ marginTop: 8, opacity: 0.85 }}>
            Upload an electricity bill, invoice, waste report, or travel file to start building your evidence library.
          </p>
          <p style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
            Tip: include keywords like <b>electricity</b>, <b>gas</b>, <b>invoice</b>, <b>travel</b> in filenames to auto-suggest tags.
          </p>
        </div>
      ) : (
        <div className="table-card" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Tag</th>
                <th>Type</th>
                <th>Uploaded</th>
                <th>Size</th>
                <th style={{ width: 260 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((d, idx) => {
                const key = d.id || d.filename || idx;
                const busy = rowBusyId && rowBusyId === (d.id || d.filename);

                return (
                  <tr key={key}>
                    <td style={{ fontWeight: 600 }}>{d.filename}</td>

                    <td style={{ minWidth: 180 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select
                          value={d.tag || ""}
                          onChange={(e) => setDocTag(d.filename, e.target.value)}
                          title={d.tagSource === "suggested" ? "Suggested from filename" : "Tag"}
                          style={{ width: "100%" }}
                        >
                          <option value="">—</option>
                          {TAGS.map((t) => (
                            <option key={t} value={t}>
                              {TAG_LABEL[t] || t}
                            </option>
                          ))}
                        </select>

                        {(tagsMap?.[d.filename] || "") && (
                          <button
                            className="btn-secondary"
                            type="button"
                            onClick={() => setDocTag(d.filename, "")}
                            title="Clear saved tag"
                            style={{ padding: "6px 10px" }}
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {d.tagSource !== "none" && (
                        <div style={{ marginTop: 6 }}>
                          <span className="pill pill-soft" style={{ fontSize: 11 }}>
                            {d.tagSource === "saved" ? "Saved tag" : "Suggested"}
                          </span>
                        </div>
                      )}
                    </td>

                    <td>{d.typeLabel}</td>
                    <td>{safeDate(d.uploaded_at)}</td>
                    <td>{bytesToSize(d.size_bytes)}</td>

                    <td style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="btn-secondary" onClick={() => handlePreview(d)} disabled={busy}>
                        {busy ? "Working…" : "Preview"}
                      </button>
                      <button className="btn-primary" onClick={() => handleDownload(d)} disabled={busy}>
                        {busy ? "Working…" : "Download"}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 14, opacity: 0.8 }}>
                    No documents match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
